import React, { useMemo, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import { FormField, FormService } from '@shared/services/FormServices';
import { RateComponentRef, FlatRate } from './FlatRate';
import { SharedFormArray } from '@shared/components/SharedFormArray';
import { SlabBased } from './SlabBased';
import { TimeOfUse } from './TimeOfUse';
import { FixedRate } from './FixedRate';

interface SeasonalProps {
  data: any;
  unit:string;
}

export const Seasonal = forwardRef<RateComponentRef, SeasonalProps>(({ data,unit }, ref) => {
  const childRefs = useRef<Record<number, RateComponentRef | null>>({});

  const seasonalFields: FormField[] = useMemo(
    () => [
      { 
        name: "seasonName", 
        label: "Season Name", 
        type: "text", 
        required: true,
        placeholder: "e.g. Summer",
        className: "md:col-span-6 lg:col-span-3",
        helperText: "Enter a name for this season (e.g. Summer, Winter)"
      },
      { 
        name: "from", 
        label: "From", 
        type: "mm-dd" as any,
        required: true,
        className: "md:col-span-6 lg:col-span-3",
        helperText: "Enter the start date of the season (MM-DD)"
      },
      { 
        name: "to", 
        label: "To", 
        type: "mm-dd" as any,
        required: true,
        className: "md:col-span-6 lg:col-span-3",
        helperText: "Enter the end date of the season (MM-DD)"
      },
      { 
        name: "rateType", 
        label: "Rate Type", 
        type: "select", 
        options: [
          { label: "Flat Rate", value: "Flat Rate" },
          { label: "Slab Based", value: "Slab Based" },
          { label: "Time of Use", value: "Time of Use" },
          { label: "Fixed", value: "Fixed" },
        ],
        required: true,
        className: "md:col-span-6 lg:col-span-3",
        helperText: "Select the rate type for this season"
      }
    ],
    []
  );

  const form = FormService.createForm({
    fields: [
      { name: "seasons", type: "form-array", itemFields: seasonalFields }
    ],
    mode: "onChange",
    validateOnMount: false,
    defaultValues: data
  });

  // Watch seasons to know which rateType is selected per row
  const seasons = form.watch("seasons") || [];

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      form.reset(data);
    }
  }, [data, form]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const isValid = await form.trigger();
      if (!isValid) return null;

      const values = form.getValues();
      const updatedSeasons = [...values.seasons];

      for (let i = 0; i < updatedSeasons.length; i++) {
        const rateType = updatedSeasons[i].rateType;
        if (rateType && childRefs.current[i]) {
          const childData = await childRefs.current[i]?.submit();
          if (!childData) {
            return null; // Child validation failed
          }
          updatedSeasons[i].rateConfig = childData;
        }
      }

      return { ...values, seasons: updatedSeasons };
    },
    isDirty: () => {
      if (Object.keys(form.formState.dirtyFields).length > 0) return true;
      for (const idx in childRefs.current) {
         if ((childRefs.current[idx] as any)?.isDirty?.()) return true;
      }
      return false;
    }
  }));

  const renderChildComponent = (index: number) => {
    const seasonData = seasons[index] || {};
    const rateType = seasonData.rateType;
    const existingConfig = seasonData.rateConfig;

    if (!rateType) return null;

    return (
      <div className="pt-2">
        {(() => {
          switch (rateType) {
            case "Flat Rate":
              return <FlatRate ref={el => { childRefs.current[index] = el; }} data={existingConfig} unit={unit} hideHeader={true} />;
            case "Slab Based":
              return <SlabBased ref={el => { childRefs.current[index] = el; }} data={existingConfig} unit={unit} hideHeader={true} />;
            case "Time of Use":
              return <TimeOfUse ref={el => { childRefs.current[index] = el; }} data={existingConfig} unit={unit} hideHeader={true} />;
            case "Fixed":
              return <FixedRate ref={el => { childRefs.current[index] = el; }} data={existingConfig} unit={unit} hideHeader={true} />;
            default:
              return null;
          }
        })()}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <SharedFormArray
        name="seasons"
        form={form}
        itemFields={seasonalFields}
        variant="card"
        gridColumns={12}
        label="Seasonal Rate Builder"
        helperText="Define one or more seasons. Each season has its own date window and chooses its own rate type — flat, slab, time-of-use or fixed."
        emptyMessage="No seasonal rates configured yet."
        addButtonText="Add Season"
        renderAfterContent={renderChildComponent}
        minItems={1}
      />
    </div>
  );
});

Seasonal.displayName = "Seasonal";
